import React, { PropsWithChildren, createContext, useCallback, useContext, useState } from 'react';

type InformationalAlert = {
    title: string;
    description: string | JSX.Element;
    icon?: string | JSX.Element;
    testId?: string;
    clearAlertText?: string;
    onDismiss?: () => void | Promise<void>;
    iconBGColor?: string;
};

type ConfirmationAlert = {
    title: string;
    description: string | JSX.Element;
    icon?: string | JSX.Element;
    iconBGColor?: string;
    testId?: string;
    confirmText: string;
    cancelText: string;
    onConfirm: () => void | Promise<void>;
    onCancel?: () => void;
    errorTitle?: string;
    errorDescription?: string;
};

type ErrorAlert = {
    errorTitle: string;
    errorDescription: string;
    icon?: string | JSX.Element;
    iconBGColor?: string;
    testId?: string;
    confirmText: string;
    cancelText?: string;
    onConfirm?: () => void | Promise<void>;
    onCancel?: () => void;
};

export enum AlertType {
    INFORMATIONAL = 'information',
    CONFIRMATION = 'confirmation',
    ERROR = 'error',
}

export type Alert =
    | ({ type: AlertType.INFORMATIONAL } & InformationalAlert)
    | ({ type: AlertType.CONFIRMATION } & ConfirmationAlert)
    | ({ type: AlertType.ERROR } & ErrorAlert);

type AlertState = { alert: Alert | undefined };

type DisplayAlertFunctions = {
    showInfoAlertToUser: (alertInfo: InformationalAlert) => void;
    showConfirmationToUser: (alertInfo: ConfirmationAlert) => void;
    showErrorToUser: (alertInfo: ErrorAlert) => void;
    clearAlert: (currentAlert?: Alert) => void;
};

export type AlertContextType = AlertState & DisplayAlertFunctions;

export const AlertContext = createContext<AlertContextType>({
    alert: undefined,
    showInfoAlertToUser: () => {},
    showConfirmationToUser: () => {},
    showErrorToUser: () => {},
    clearAlert: () => {},
});

export const AlertProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [alertState, setAlertState] = useState<AlertState>({
        alert: undefined,
    });

    const showInfoAlertToUser = useCallback(
        (alertInfo: InformationalAlert) => {
            setAlertState({ alert: { ...alertInfo, type: AlertType.INFORMATIONAL } });
        },
        [setAlertState]
    );

    const showConfirmationToUser = useCallback(
        (alertInfo: ConfirmationAlert) => {
            setAlertState({ alert: { ...alertInfo, type: AlertType.CONFIRMATION } });
        },
        [setAlertState]
    );

    const showErrorToUser = useCallback(
        (alertInfo: ErrorAlert) => {
            setAlertState({ alert: { ...alertInfo, type: AlertType.ERROR } });
        },
        [setAlertState]
    );

    const clearAlert = useCallback(
        (currentAlert?: Alert) => {
            if (currentAlert !== undefined) {
                setAlertState((prev) => (prev.alert === currentAlert ? { alert: undefined } : prev));
            } else {
                setAlertState({ alert: undefined });
            }
        },
        [setAlertState]
    );

    return (
        <AlertContext.Provider
            value={{
                ...alertState,
                showInfoAlertToUser,
                showConfirmationToUser,
                showErrorToUser,
                clearAlert,
            }}
        >
            {children}
        </AlertContext.Provider>
    );
};

export const useAlert = (): typeof context => {
    const context = useContext(AlertContext);

    if (context === null) {
        throw new Error('useAlert must be used within an alert context provider.');
    }
    return context;
};
